import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Plus, Trash2, X, FileText, Edit, Settings2 } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { AddWelderQualificationRowModal } from './AddWelderQualificationRowModal';
import type { WelderQualificationRowDocument, WelderPipeWpsLine } from './AddWelderQualificationRowModal';
import {
  bulkDeleteWelderQualification,
  deleteWelderQualificationRowsByIds,
  getAllWelderQualification,
} from '../../../../../redux/Project/registrySaga';
import { DocumentDownloadModal } from '../production/DocumentDownloadModal';
import { DeleteConfirmationDialog } from '../../../../../components/ui/DeleteConfirmationDialog';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';

const STORAGE_PREFIX = 'welderQualificationRegister_v1';

const QUALIFICATION_OPTIONS = [
  { value: '-', label: '— (Not qualified)' },
  { value: 'WPQT', label: 'WPQT' },
  { value: 'WQT XX', label: 'WQT XX' },
  { value: 'WPS XX', label: 'WPS XX' },
] as const;

function qualificationLabel(code: string | undefined): string {
  const c = code ?? '-';
  const o = QUALIFICATION_OPTIONS.find((x) => x.value === c);
  return o?.label ?? c;
}

function newId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Normalize getAllWelderQualifications response to a flat list of records. */
function welderQualApiListToArray(data: unknown): Record<string, unknown>[] {
  if (data == null) return [];
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.items)) return o.items as Record<string, unknown>[];
    if (Array.isArray(o.records)) return o.records as Record<string, unknown>[];
    if (Array.isArray(o.data)) return o.data as Record<string, unknown>[];
    const out: Record<string, unknown>[] = [];
    for (const v of Object.values(o)) {
      if (v && typeof v === 'object') {
        const g = v as Record<string, unknown>;
        if (Array.isArray(g.welderQualifications))
          out.push(...(g.welderQualifications as Record<string, unknown>[]));
        if (Array.isArray(g.rows)) out.push(...(g.rows as Record<string, unknown>[]));
        if (Array.isArray(g.personnel)) out.push(...(g.personnel as Record<string, unknown>[]));
      }
    }
    if (out.length) return out;
  }
  return [];
}

function apiWelderRecordToRow(r: Record<string, unknown>): WelderQualificationRow {
  const linesRaw = r.pipe_wps_lines ?? r.pipeWpsLines;
  const pipeWpsLines: WelderPipeWpsLine[] | undefined = Array.isArray(linesRaw)
    ? (linesRaw as Record<string, unknown>[]).map((l) => ({
      pipeSizeDisplay: (l.pipe_size_display ?? l.pipeSizeDisplay) as string | undefined,
      pipeId:
        l.pipe_id != null
          ? String(l.pipe_id)
          : l.pipeId != null
            ? String(l.pipeId)
            : null,
      projectTypeId:
        l.project_type_id != null
          ? String(l.project_type_id)
          : l.projectTypeId != null
            ? String(l.projectTypeId)
            : null,
      wpsSpecificationId:
        l.wps_specification_id != null
          ? String(l.wps_specification_id)
          : l.wpsSpecificationId != null
            ? String(l.wpsSpecificationId)
            : null,
      wpsSpecificationLabel: (l.wps_specification_label ?? l.wpsSpecificationLabel ?? null) as string | null,
      qualificationType: (l.qualification_type ?? l.qualificationType ?? null) as string | null,
      scopeOfWorkType: (l.scope_of_work_type ?? l.scopeOfWorkType ?? null) as string | null,
      workType: (l.work_type ?? l.workType ?? null) as string | null,
    }))
    : undefined;

  let qualifications: Record<string, string> = {};
  const qRaw = r.qualifications ?? r.wps_qualifications;
  if (qRaw && typeof qRaw === 'object' && !Array.isArray(qRaw)) {
    qualifications = Object.fromEntries(
      Object.entries(qRaw as Record<string, unknown>).map(([k, v]) => [k, String(v ?? '-')])
    );
  } else if (typeof qRaw === 'string') {
    try {
      const p = JSON.parse(qRaw) as Record<string, unknown>;
      if (p && typeof p === 'object') {
        qualifications = Object.fromEntries(
          Object.entries(p).map(([k, v]) => [k, String(v ?? '-')])
        );
      }
    } catch {
      /* ignore */
    }
  }

  const id = r.id != null ? String(r.id) : newId('wq');

  return {
    id,
    weldNumber: String(r.weld_number ?? r.weldNumber ?? ''),
    welderName: String(r.welder_name ?? r.welderName ?? ''),
    welderId:
      r.welder_id != null
        ? Number(r.welder_id)
        : r.welderId != null
          ? Number(r.welderId)
          : null,
    operatorUserId:
      r.operator_user_id != null
        ? Number(r.operator_user_id)
        : r.operatorUserId != null
          ? Number(r.operatorUserId)
          : null,
    qualifications,
    notes: (r.notes as string | undefined) ?? undefined,
    documents: Array.isArray(r.documents) ? (r.documents as WelderQualificationRowDocument[]) : undefined,
    pipeWpsLines: pipeWpsLines?.length ? pipeWpsLines : undefined,
    pipeSizeDisplay: (r.pipe_size_display ?? r.pipeSizeDisplay) as string | undefined,
    pipeId: r.pipe_id != null ? String(r.pipe_id) : r.pipeId != null ? String(r.pipeId) : null,
    projectTypeId:
      r.project_type_id != null
        ? String(r.project_type_id)
        : r.projectTypeId != null
          ? String(r.projectTypeId)
          : undefined,
    wpsSpecificationId:
      r.wps_specification_id != null
        ? String(r.wps_specification_id)
        : r.wpsSpecificationId != null
          ? String(r.wpsSpecificationId)
          : null,
    wpsSpecificationLabel: (r.wps_specification_label ?? r.wpsSpecificationLabel ?? null) as string | null,
  };
}

export interface WpsColumnDef {
  id: string;
  wpsLabel: string;
  od: string;
  wt: string;
  scope: string;
}

export interface WelderQualificationRow {
  id: string;
  weldNumber: string;
  welderName: string;
  welderId: number | null;
  operatorUserId: number | null;
  qualifications: Record<string, string>;
  notes?: string;
  documents?: WelderQualificationRowDocument[];
  /** Display label from project pipe setup (Materials / setupData). */
  pipeSizeDisplay?: string;
  pipeId?: string | null;
  projectTypeId?: string | null;
  /** Selected WPS specification from project documents (type WPS). */
  wpsSpecificationId?: string | null;
  wpsSpecificationLabel?: string | null;
  /** Multiple pipe + WPS lines from the modal (preferred over single fields above). */
  pipeWpsLines?: WelderPipeWpsLine[];
}

function rowWpsSpecSummary(row: WelderQualificationRow): string {
  if (row.pipeWpsLines?.length) {
    return row.pipeWpsLines.map((l) => l.wpsSpecificationLabel).filter(Boolean).join('; ');
  }
  return row.wpsSpecificationLabel || '';
}

type QualificationLineCard = {
  pipe: string;
  wps: string;
  scope: string;
  work: string;
};

function getQualificationLineCards(row: WelderQualificationRow): QualificationLineCard[] {
  if (row.pipeWpsLines?.length) {
    return row.pipeWpsLines.map((l) => ({
      pipe: (l.pipeSizeDisplay || '').trim() || '—',
      wps: (l.wpsSpecificationLabel || '').trim() || '—',
      scope: (l.scopeOfWorkType || l.qualificationType || '').trim() || '—',
      work: (l.workType || '').trim() || '—',
    }));
  }
  const pipe = (row.pipeSizeDisplay || '').trim() || '—';
  const wps = (row.wpsSpecificationLabel || '').trim() || '—';
  if (pipe !== '—' || wps !== '—') {
    return [{ pipe, wps, scope: '—', work: '—' }];
  }
  return [];
}

function QualificationLineCardView({ line, index }: { line: QualificationLineCard; index: number }) {
  const Cell = ({ label, value }: { label: string; value: string }) => (
    <div className="min-w-0">
      <p className="text-xs text-gray-900 leading-snug">
        <span className="font-semibold text-gray-800">{label}: </span>
        <span className="text-gray-700 break-words">{value}</span>
      </p>
    </div>
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <p className="text-xs font-medium text-gray-500 mb-2">Qualification {index + 1}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Cell label="Pipe size" value={line.pipe} />
        <Cell label="WPS number" value={line.wps} />
        <Cell label="Scope of work" value={line.scope} />
        <Cell label="Work type" value={line.work} />
      </div>
    </div>
  );
}

interface StoredShape {
  wpsColumns: WpsColumnDef[];
  customWelderNames: string[];
}

const defaultStored = (): StoredShape => ({
  wpsColumns: [
    {
      id: newId('wps'),
      wpsLabel: 'WPS-01',
      od: '',
      wt: '',
      scope: '',
    },
  ],
  customWelderNames: [],
});

export interface WelderQualificationRegisterTableHandle {
  openAddWelderRow: () => void;
}

interface WelderQualificationRegisterTableProps {
  project: DashboardProject;
  canEdit?: boolean;
  /** When false, hide the table toolbar "Add welder row" (e.g. parent provides the button). */
  showTableToolbarAddButton?: boolean;
}

export const WelderQualificationRegisterTable = forwardRef<
  WelderQualificationRegisterTableHandle,
  WelderQualificationRegisterTableProps
>(function WelderQualificationRegisterTable(
  { project, canEdit = true, showTableToolbarAddButton = true },
  ref
) {
  const projectId = project?.project?.projectId;
  const storageKey = projectId ? `${STORAGE_PREFIX}_${projectId}` : null;

  const [wpsColumns, setWpsColumns] = useState<any[]>([]);
  const [rows, setRows] = useState<WelderQualificationRow[]>([]);
  const [customWelderNames, setCustomWelderNames] = useState<string[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isWpsManageOpen, setIsWpsManageOpen] = useState(false);
  const [isRowModalOpen, setIsRowModalOpen] = useState(false);
  const [welderRowToEdit, setWelderRowToEdit] = useState<WelderQualificationRow | null>(null);
  const [selectedRowForDocs, setSelectedRowForDocs] = useState<WelderQualificationRow | null>(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<WelderQualificationRow | null>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [rowDeleteLoading, setRowDeleteLoading] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [isDocumentDeleteModalOpen, setIsDocumentDeleteModalOpen] = useState(false);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);

  useEffect(() => {
    if (!storageKey) {
      setHydrated(true);
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<StoredShape> & { rows?: unknown };
        if (parsed.wpsColumns?.length) {
          setWpsColumns(parsed.wpsColumns);
        } else {
          setWpsColumns(defaultStored().wpsColumns);
        }
        setCustomWelderNames(Array.isArray(parsed.customWelderNames) ? parsed.customWelderNames : []);
      } else {
        const d = defaultStored();
        setWpsColumns(d.wpsColumns);
        setCustomWelderNames(d.customWelderNames);
      }
    } catch {
      const d = defaultStored();
      setWpsColumns(d.wpsColumns);
    }
    setHydrated(true);
  }, [storageKey]);

  const refreshWelderRows = useCallback(async (options?: { silent?: boolean }) => {
    if (!projectId) return;
    const silent = options?.silent === true;
    if (!silent) setRowsLoading(true);
    try {
      const data = await getAllWelderQualification({ project_id: Number(projectId) });
      const list = welderQualApiListToArray(data);
      setRows(list.map((rec) => apiWelderRecordToRow(rec)));
    } catch {
      customNotification.error('Load failed', 'Could not load welder qualifications. Please try again.');
    } finally {
      if (!silent) setRowsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!hydrated || !projectId) return;
    void refreshWelderRows();
  }, [hydrated, projectId, refreshWelderRows]);

  const persist = useCallback(
    (next: { wpsColumns: WpsColumnDef[]; customWelderNames: string[] }) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            wpsColumns: next.wpsColumns,
            customWelderNames: next.customWelderNames,
          })
        );
      } catch {
        /* ignore quota */
      }
    },
    [storageKey]
  );

  useEffect(() => {
    if (!hydrated || !storageKey) return;
    persist({ wpsColumns, customWelderNames });
  }, [wpsColumns, customWelderNames, hydrated, persist, storageKey]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const lineHay =
        r.pipeWpsLines?.map((l) =>
          [
            l.pipeSizeDisplay,
            l.wpsSpecificationLabel,
            l.scopeOfWorkType,
            l.workType,
            l.qualificationType,
          ]
            .filter(Boolean)
            .join(' ')
        ) ?? [];
      const hay = [
        r.weldNumber,
        r.welderName,
        r.notes ?? '',
        r.pipeSizeDisplay ?? '',
        r.wpsSpecificationLabel ?? '',
        ...lineHay,
        ...Object.values(r.qualifications),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, searchQuery]);

  const addWpsColumn = () => {
    const id = newId('wps');
    setWpsColumns((prev) => {
      const n = prev.length + 1;
      const label = `WPS-${String(n).padStart(2, '0')}`;
      return [...prev, { id, wpsLabel: label, od: '', wt: '', scope: '' }];
    });
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        qualifications: { ...r.qualifications, [id]: '-' },
      }))
    );
  };

  const removeWpsColumn = (id: string) => {
    setWpsColumns((prev) => prev.filter((c) => c.id !== id));
    setRows((prev) =>
      prev.map((r) => {
        const { [id]: _, ...rest } = r.qualifications;
        return { ...r, qualifications: rest };
      })
    );
  };

  const updateWpsField = (
    id: string,
    field: keyof Pick<WpsColumnDef, 'wpsLabel' | 'od' | 'wt' | 'scope'>,
    value: string
  ) => {
    setWpsColumns((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const addCustomWelderNameOnly = (name: string) => {
    setCustomWelderNames((prev) => {
      if (prev.some((n) => n.toLowerCase() === name.toLowerCase())) return prev;
      return [...prev, name];
    });
  };

  const openAddModal = useCallback(() => {
    setWelderRowToEdit(null);
    setIsRowModalOpen(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openAddWelderRow: openAddModal,
    }),
    [openAddModal]
  );

  const openEditModal = (row: WelderQualificationRow) => {
    setWelderRowToEdit(row);
    setIsRowModalOpen(true);
  };

  const closeRowModal = () => {
    setIsRowModalOpen(false);
    setWelderRowToEdit(null);
  };

  const transformDocumentsForDownload = (docs: WelderQualificationRowDocument[] | undefined) => {
    if (!docs?.length) return [];
    return docs.map((doc: any, index: number) => ({
      id: doc.welder_qualification_doc_id,
      fileName: doc.fileName,
      fileUrl: doc.fileUrl || doc.file_url || '',
      docId: doc.docRef ?? doc.docRefId ?? doc.s3Key ?? null,
    }));
  };

  const handleDocumentDownload = (doc: any) => {
    const url = doc.fileUrl || doc.file_url;
    if (url) {
      window.open(url, '_blank');
    } else {
      customNotification.info(
        'Download',
        'No download link is stored for this file. Re-upload from the welder row editor if needed.'
      );
    }
  };

  const handleDocumentClick = (row: WelderQualificationRow) => {
    setSelectedRowForDocs(row);
    setIsDownloadModalOpen(true);
  };

  const handleDeleteClick = (row: WelderQualificationRow) => {
    setRowToDelete(row);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteRow = async () => {
    if (!rowToDelete?.id) return;
    setRowDeleteLoading(true);
    try {
      const idNum = Number(rowToDelete.id);
      const payload = { ids: [Number.isFinite(idNum) ? idNum : rowToDelete.id] };
      const res = await bulkDeleteWelderQualification(payload);
      if (res) {
        setIsDeleteModalVisible(false);
        setRowToDelete(null);
        await refreshWelderRows({ silent: true });
      }
    } finally {
      setRowDeleteLoading(false);
    }
  };

  const handleDeleteDocument = (doc: any) => {
    console.log('doc', doc);
    setDocumentToDelete(doc);
    setIsDocumentDeleteModalOpen(true);
  };

  const handleCancelDocumentDelete = () => {
    setIsDocumentDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDocumentDelete = async () => {
    if (!documentToDelete) return;
    console.log('documentToDelete', documentToDelete);
    const rawDocId = documentToDelete.docId ?? documentToDelete.doc_id ?? documentToDelete.id;
    if (!rawDocId) {
      customNotification.error('Delete failed', 'Document ID not found.');
      return;
    }


    setDeleteDocumentLoading(true);
    try {
      const idNum = Number(rawDocId);
      const payload = { docIds: [Number.isFinite(idNum) ? idNum : rawDocId] };
      const res = await deleteWelderQualificationRowsByIds(payload);
      if (res) {
        setIsDocumentDeleteModalOpen(false);
        setDocumentToDelete(null);
        await refreshWelderRows({ silent: true });
        setIsDownloadModalOpen(false);
        setSelectedRowForDocs(null);
      }
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  /** Weld, Welder, WPS summary, Qualifications (cards), Notes, Documents [, Actions]. */
  const colSpan = 6 + (canEdit ? 1 : 0);

  if (!projectId) {
    return (
      <Card>
        <CardContent className="p-6 text-gray-500">Project is required to use this register.</CardContent>
      </Card>
    );
  }

  if (!hydrated || rowsLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-gray-500">Loading register…</CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">


            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-end gap-3 mb-4 w-full">
              {canEdit && showTableToolbarAddButton && (
                <div className="flex flex-wrap gap-2 justify-end w-full sm:w-auto">
                  <Button
                    type="button"
                    size="sm"
                    className="bg-[#e04530] text-white hover:bg-[#e04530]/90"
                    onClick={openAddModal}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add welder row
                  </Button>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Weld number</TableHead>
                    <TableHead>Welder name</TableHead>
                    <TableHead className="min-w-[300px] max-w-[420px]">Qualifications</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-center w-[80px]">Documents</TableHead>
                    {canEdit && (
                      <TableHead className="text-center w-[100px] sticky right-0 z-10 bg-white">
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colSpan} className="text-center text-gray-500 py-10">
                        No welders yet. Click &quot;Add welder row&quot; to create an entry.
                      </TableCell>
                    </TableRow>
                  ) : filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colSpan} className="text-center text-gray-500 py-8">
                        No rows match your search.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row, index) => {
                      const docCount = row.documents?.length ?? 0;
                      const qualCards = getQualificationLineCards(row);
                      return (
                        <TableRow key={row.id || index}>
                          <TableCell>{row.weldNumber || '—'}</TableCell>
                          <TableCell>{row.welderName || '—'}</TableCell>

                          <TableCell className="align-top max-w-[420px] py-3">
                            {qualCards.length > 0 ? (
                              <div className="space-y-3">
                                {qualCards.map((line, i) => (
                                  <QualificationLineCardView
                                    key={`${row.id}-qual-${i}`}
                                    line={line}
                                    index={i}
                                  />
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[220px]">
                            <span className="line-clamp-2 whitespace-pre-wrap" title={row.notes || undefined}>
                              {row.notes || '—'}
                            </span>
                          </TableCell>
                          <TableCell className="text-center w-[80px]">
                            {docCount > 0 ? (
                              <Button
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-auto min-w-[2.5rem] h-8 px-2 py-0 justify-center mx-auto"
                                title="View documents"
                                variant="outline"
                                onClick={() => handleDocumentClick(row)}
                              >
                                <FileText className="h-4 w-4" />
                                {docCount}
                              </Button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </TableCell>
                          {canEdit && (
                            <TableCell className="text-center w-[100px] sticky right-0 z-10 bg-white">
                              <div className="flex items-center gap-2 justify-center">
                                <Button
                                  type="button"
                                  onClick={() => openEditModal(row)}
                                  className="text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                  title="Edit welder row"
                                  variant="outline"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                                  onClick={() => handleDeleteClick(row)}
                                  title="Delete row"
                                  variant="outline"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>


          </CardContent>
        </Card>
      </div>
      <DeleteConfirmationDialog
        isOpen={isDeleteModalVisible}
        onClose={() => {
          setIsDeleteModalVisible(false);
          setRowToDelete(null);
        }}
        onConfirm={handleDeleteRow}
        isLoading={rowDeleteLoading}
        itemName={rowToDelete ? `${rowToDelete.weldNumber} — ${rowToDelete.welderName}` : ''}
        itemType="welder row"
        title="Delete welder row"
      />

      <DeleteConfirmationDialog
        isOpen={isDocumentDeleteModalOpen}
        onClose={handleCancelDocumentDelete}
        onConfirm={handleConfirmDocumentDelete}
        isLoading={deleteDocumentLoading}
        itemToDelete={documentToDelete}
        itemType="Welder Document"
        itemName={documentToDelete?.fileName || documentToDelete?.file_name || ''}
        title="Delete welder document"
      />

      {isDownloadModalOpen && <DocumentDownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => {
          setIsDownloadModalOpen(false);
          setSelectedRowForDocs(null);
        }}
        documents={transformDocumentsForDownload(selectedRowForDocs?.documents)}
        onDownload={handleDocumentDownload}
        onDelete={handleDeleteDocument}
        canDelete={canEdit}
        title={selectedRowForDocs ? `Welder Documents - ${selectedRowForDocs.welderName}` : 'Welder Documents'}
      />
      }
      <AddWelderQualificationRowModal
        isOpen={isRowModalOpen}
        onClose={closeRowModal}
        onSaved={() => void refreshWelderRows({ silent: true })}
        welderRowToEdit={welderRowToEdit}
        project={project}
        wpsColumns={wpsColumns}
        customWelderNames={customWelderNames}
        onAddCustomWelderName={addCustomWelderNameOnly}
        qualificationList={rows}
      />
    </>
  );
});
